import {NgModule} from '@angular/core';
import {BrowserModule, provideClientHydration} from '@angular/platform-browser';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';

import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {MatTableModule} from '@angular/material/table'
import {MatChip} from '@angular/material/chips'
import {MatPaginator} from '@angular/material/paginator'
import {MatFormField, MatFormFieldModule, MatLabel} from '@angular/material/form-field'
import {MatInput} from '@angular/material/input'
import {MatSelectModule} from '@angular/material/select';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatDialogModule} from '@angular/material/dialog';
import {MatSortModule} from '@angular/material/sort';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatCardModule} from '@angular/material/card';
import {MatIconModule} from '@angular/material/icon';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatTabsModule} from '@angular/material/tabs';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatRadioModule} from '@angular/material/radio';
import {MatMenuModule} from '@angular/material/menu';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatListModule} from '@angular/material/list';
import { SortablejsDirective, provideGlobalSortableOptions } from 'nxt-sortablejs';

import {HeaderComponent} from './components/header/header.component';
import {FooterComponent} from './components/footer/footer.component';
import {DashboardComponent} from './routes/dashboard/dashboard.component';
import {NavComponent} from './components/nav/nav.component';
import {BannerComponent} from './components/banner/banner.component';
import {FireTrackerComponent, MergeSnackbarComponent} from './routes/fire-tracker/fire-tracker.component';
import {NewFireComponent} from './routes/fire-tracker/new-fire/new-fire.component';
import {EditFireComponent} from './routes/fire-tracker/edit-fire/edit-fire.component';
import {DateAdapter, MatNativeDateModule} from '@angular/material/core';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {MatSnackBarAction, MatSnackBarActions, MatSnackBarLabel} from '@angular/material/snack-bar';
import {
  ConfirmationToastComponent,
  ErrorToastComponent,
  SuccessToastComponent
} from './components/confirmation-toast/confirmation-toast.component';
import {DataManipulationComponent} from './routes/data-manipulation/data-manipulation.component';
import {RosterComponent} from './routes/roster/roster.component';
import {DeleteUserSnackbarComponent, UserRosterComponent} from './routes/roster/user-roster/user-roster.component';
import {PhonePipe} from 'src/pipes/phone.pipe';
import {SkillsPipe} from 'src/pipes/skills.pipe';
import {ForestFilterPipe} from 'src/pipes/forest_filter.pipe';
import {MergeFiresComponent} from './routes/fire-tracker/merge-fires/merge-fires.component';
import {MatAutocompleteModule} from '@angular/material/autocomplete'
import {ComplexityModalComponent} from './routes/fire-tracker/complexity-modal/complexity-modal.component';
import {LineBreakPipe} from 'src/pipes/line_break.pipe';
import {BoolPipe} from 'src/pipes/bool.pipe';
import {ProgressSpinnerComponent} from './components/progress-spinner/progress-spinner.component';
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {UserComponent} from './routes/user/user.component';
import {ViewContactComponent} from './routes/user/view-contact/view-contact.component';
import {ViewAvailabilityComponent} from './routes/user/view-availability/view-availability.component';
import {ViewSkillsComponent} from './routes/user/view-skills/view-skills.component';
import {EditContactComponent} from './routes/user/edit-contact/edit-contact.component';
import {EditAvailabilityComponent} from './routes/user/edit-availability/edit-availability.component';
import {EditSkillsComponent} from './routes/user/edit-skills/edit-skills.component';
import {ViewAssignmentsComponent} from './routes/user/view-assignments/view-assignments.component';
import {NewUserComponent} from './routes/user/new-user/new-user.component';
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';
import {DeleteUserComponent} from './routes/user/delete-user/delete-user.component';
import {LoginRedirectComponent} from "./routes/login-redirect/login-redirect.component";
import {LoginComponent} from "./routes/login/login.component";
import {MatGridList, MatGridListModule, MatGridTile} from "@angular/material/grid-list";
import {FocusableDirective} from './directive/focusable.directive';
import {RemoveUserComponent} from './routes/user/remove-user/remove-user.component';
import {AuthInterceptor} from "./interceptors/auth.interceptor";
import {HeaderMenuComponent} from './components/header/header-menu/header-menu.component';
import {EditSelfComponent} from './routes/user/edit-self/edit-self.component';
import {TextFieldModule} from '@angular/cdk/text-field';
import {EditAssessmentComponent} from './routes/assessments/edit-assessment/edit-assessment.component';
import {AssessmentsComponent} from './routes/assessments/assessments.component';
import {ReportListComponent} from './routes/assessments/report-list/report-list.component';
import {StatusBadgePipe} from 'src/pipes/status_badge.pipe';
import {RequestSupportComponent} from './routes/assessments/request-support/request-support.component';
import {ViewFireComponent} from './routes/fire-overview/reports-tab/view-fire/view-fire.component';
import {ViewTeamNeedsComponent} from './routes/fire-overview/reports-tab/view-team-needs/view-team-needs.component';
import {ViewReportsComponent} from './routes/fire-overview/reports-tab/view-reports/view-reports.component';
import {
  ViewForestContactsComponent
} from './routes/fire-overview/reports-tab/view-forest-contacts/view-forest-contacts.component';
import {FireOverviewComponent} from './routes/fire-overview/fire-overview.component';
import {ReportsTabComponent} from './routes/fire-overview/reports-tab/reports-tab.component';
import {SupportRequestsTabComponent} from './routes/assessments/support-requests-tab/support-requests-tab.component';
import {BaerAssessmentsTabComponent} from './routes/assessments/baer-assessments-tab/baer-assessments-tab.component';
import {CustomDateAdapter} from "./adapters/custom-date.adapter";
import {ReportStatusBadgePipe} from 'src/pipes/report_status_badge';
import {DATE_PIPE_DEFAULT_OPTIONS} from "@angular/common";
import {TeamsTabComponent} from './routes/fire-overview/teams-tab/teams-tab.component';
import {DemobilizeComponent} from './routes/fire-overview/teams-tab/demobilize/demobilize.component';
import {AddTeamMemberComponent} from './routes/fire-overview/teams-tab/add-team-member/add-team-member.component';
import {RemoveMemberComponent} from './routes/fire-overview/teams-tab/remove-member/remove-member.component';
import {Report25008Component} from './routes/report-2500-8/report-2500-8.component';
import {ReportTypeComponent} from './routes/report-2500-8/report-type/report-type.component';
import {
  ReportTypeModalComponent
} from './routes/report-2500-8/report-type/report-type-modal/report-type-modal.component';
import {
  FullPhasedModalComponent
} from './routes/report-2500-8/report-type/full-phased-modal/full-phased-modal.component';
import {BurnedAreaComponent} from './routes/report-2500-8/burned-area/burned-area.component';
import {
  EmergencyStabilizationComponent
} from './routes/report-2500-8/emergency-stabilization/emergency-stabilization.component';
import {WatershedConditionComponent} from './routes/report-2500-8/watershed-condition/watershed-condition.component';
import {NgxEditorModule} from 'ngx-editor';
import {AnalysisSummaryComponent} from './routes/report-2500-8/analysis-summary/analysis-summary.component';
import {ImageUploadComponent} from './routes/report-2500-8/image-upload/image-upload.component';
import {
  RecommendedTreatmentComponent
} from './routes/report-2500-8/recommended-treatment/recommended-treatment.component';
import {BytePipe} from 'src/pipes/byte.pipe';
import {TeamSupportingComponent} from './routes/report-2500-8/team-supporting/team-supporting.component';
import {NewCommentComponent} from './routes/report-2500-8/new-comment/new-comment.component';
import {DeleteCommentComponent} from './routes/report-2500-8/delete-comment/delete-comment.component';
import {SelectLineOfficerComponent} from './routes/report-2500-8/select-line-officer/select-line-officer.component';
import {SubmissionComponent} from './routes/report-2500-8/submission/submission.component';
import {LocationFilterComponent} from './routes/user/new-user/location-filter/location-filter.component';
import {ViewReport25008Component} from './routes/report-2500-8/view-sections/view-report/view-report-2500-8.component';
import {ViewReportTypeComponent} from './routes/report-2500-8/view-sections/report-type/view-report-type.component';
import {
  ViewWatershedConditionComponent
} from './routes/report-2500-8/view-sections/view-watershed-condition/view-watershed-condition.component';
import {
  ViewAnalysisSummaryComponent
} from './routes/report-2500-8/view-sections/view-analysis-summary/view-analysis-summary.component';
import {
  ViewRecommendedTreatmentComponent
} from './routes/report-2500-8/view-sections/view-recommended-treatment/view-recommended-treatment.component';
import {
  ViewEmergencyStabilizationComponent
} from './routes/report-2500-8/view-sections/view-emergency-stabilization/view-emergency-stabilization.component';
import {
  ViewTeamSupportingComponent
} from './routes/report-2500-8/view-sections/view-team-supporting/view-team-supporting.component';
import {
  ViewSelectLineOfficerComponent
} from './routes/report-2500-8/view-sections/view-select-line-officer/view-select-line-officer.component';
import {
  ViewBurnedAreaComponent
} from './routes/report-2500-8/view-sections/view-burned-area/view-burned-area.component';
import {ViewPositionsComponent} from './routes/user/view-positions/view-positions.component';
import {AuthorizationStatusBadgePipe} from 'src/pipes/authorization_status_badge';
import {ViewImageComponent} from './routes/report-2500-8/view-image/view-image.component';
import {
  ViewFullSizeImageModalComponent
} from './routes/report-2500-8/view-image/view-full-size-image/view-full-size-image.component';
import {
  DeleteTreatmentComponent
} from './routes/report-2500-8/recommended-treatment/delete-treatment/delete-treatment.component';
import {EmptyCheckPipe} from 'src/pipes/empty_check.pipe';
import {SignReportComponent} from './routes/report-2500-8/view-sections/view-report/sign-report/sign-report.component';
import {
  ViewLOSignaturesComponent
} from './routes/report-2500-8/view-sections/view-lo-signatures/view-lo-signatures.component';
import {
  QuickConfirmSnackbarComponent,
  RegionalFundingComponent
} from './routes/report-2500-8/funding/regional-funding/regional-funding.component';
import {
  QuickConfirmSnackbarComponentWO,
  WOFundingComponent
} from './routes/report-2500-8/funding/wo-funding/wo-funding.component';
import {EditFundsComponent} from './routes/report-2500-8/funding/edit-funds/edit-funds.component';
import {
  SelectRegionalOfficerComponent
} from './routes/report-2500-8/funding/regional-funding/select-regional-officer/select-regional-officer.component';
import {
  ViewROSignaturesComponent
} from './routes/report-2500-8/funding/regional-funding/view-ro-signatures/view-ro-signatures.component';
import {AuthorizationMethodPipe} from 'src/pipes/authorization_method.pipe';
import {
  ReportManipulationComponent
} from './routes/data-manipulation/report-manipulation/report-manipulation.component';
import {
  SelectWOOfficerComponent
} from './routes/report-2500-8/funding/wo-funding/select-wo-officer/select-wo-officer.component';
import {NAFilterPipe, NAUnfilterPipe} from 'src/pipes/naFilter.pipe';
import {
  ViewWOSignaturesComponent
} from './routes/report-2500-8/funding/wo-funding/view-wo-signatures/view-wo-signatures.component';
import {AssessmentCostComponent} from './routes/report-2500-8/assessment-cost/assessment-cost.component';
import {
  ViewAssessmentCostComponent
} from './routes/report-2500-8/view-sections/view-assessment-cost/view-assessment-cost.component';
import {
  PrintReport25008Component
} from './routes/report-2500-8/view-sections/print-report/print-report-2500-8.component';
import {MyDashboardComponent} from './routes/dashboard/my-dashboard/my-dashboard.component';
import {TeamAssignmentsComponent} from './routes/dashboard/my-dashboard/team-assignments/team-assignments.component';
import {LineAssignmentsComponent} from './routes/dashboard/my-dashboard/line-assignments/line-assignments.component';
import {
  ViewRegionalOfficerComponent
} from './routes/report-2500-8/funding/regional-funding/view-regional-officer/view-regional-officer.component';
import {
  ViewWOOfficerComponent
} from './routes/report-2500-8/funding/wo-funding/view-wo-officer/view-wo-officer.component';
import { CoorAssignmentsComponent } from './routes/dashboard/my-dashboard/coor-assignments/coor-assignments.component';
import {DeleteFireComponent} from "./routes/fire-tracker/delete-fire/delete-fire.component";
import { SupportsComponent } from './routes/dashboard/my-dashboard/supports/supports.component';
import { CurrentStatusComponent } from './routes/dashboard/current-status/current-status.component';
import { StatusRollupComponent } from './routes/dashboard/status-rollup/status-rollup.component';
import { StatusSummaryComponent } from './routes/dashboard/current-status/status-summary/status-summary.component';
import { RemovePositionsComponent } from './routes/user/edit-self/remove-positions/remove-positions.component';
import {MatSidenavModule} from "@angular/material/sidenav";
import { TreatmentChartComponent } from './routes/dashboard/current-status/treatment-chart/treatment-chart.component';
// import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthStatusComponent } from './routes/dashboard/auth-status/auth-status.component';
import { AuthReportListComponent } from './routes/dashboard/auth-status/auth-report-list/auth-report-list.component';
import {BarcRequestComponent} from "./routes/assessments/barc-request/barc-request.component";
import { AuthFundingComponent } from './routes/report-2500-8/emergency-stabilization/ro-auth-funding/ro-auth-funding.component';
import { FundingTabComponent } from './routes/fire-overview/funding-tab/funding-tab.component';
import {SupportComponent} from "./routes/support/support.component";
import { AnnualCompareComponent } from './routes/dashboard/annual-compare/annual-compare.component';
import { AccomplishmentsComponent } from './routes/fire-overview/accomplishments/accomplishments.component';
import { ReportAccomplishmentComponent } from './routes/report-accomplishment/report-accomplishment.component';
import { AccomplishmentDetailsComponent } from './routes/report-accomplishment/accomplishment-details/accomplishment-details.component';
import { AccomplishmentTreatmentsComponent } from './routes/report-accomplishment/accomplishment-treatments/accomplishment-treatments.component';
import { AccomplishmentAdditionalComponent } from './routes/report-accomplishment/accomplishment-additional/accomplishment-additional.component';
import { AccomplishmentSubmissionComponent } from './routes/report-accomplishment/accomplishment-submission/accomplishment-submission.component';
import { ViewAccomplishmentDetailsComponent } from './routes/report-accomplishment/view-sections/view-accomplishment-details/view-accomplishment-details.component';
import { ViewReportAccomplishmentComponent } from './routes/report-accomplishment/view-sections/view-report-accomplishment/view-report-accomplishment.component';
import { ViewAccomplishmentTreatmentsComponent } from './routes/report-accomplishment/view-sections/view-accomplishment-treatments/view-accomplishment-treatments.component';
import { ViewAccomplishmentAdditionalComponent } from './routes/report-accomplishment/view-sections/view-accomplishment-additional/view-accomplishment-additional.component';
import { PrintReportAccomplishmentComponent } from './routes/report-accomplishment/view-sections/print-report-accomplishment/print-report-accomplishment.component';
import {SafeIdPipe} from "../pipes/safe_id.pipe";
import {EditCertificationsComponent} from "./routes/user/edit-certifications/edit-certifications.component";
import { ReportsComponent } from './routes/reports/reports.component';
import { ReportsColumnsComponent } from './routes/reports/reports-columns/reports-columns.component';
import {
  AuthorizedActivitiesComponent
} from "./routes/dashboard/my-dashboard/authorized-activities/authorized-activities.component";
import {
  ExportAuthActivitiesComponent
} from "./routes/dashboard/my-dashboard/export-auth-activities/export-auth-activities.component";
import { ReportsFiltersComponent } from './routes/reports/reports-filters/reports-filters.component';
import { BarHorizontalComponent, BarHorizontalStackedComponent } from '@glitchtip/ng-charts';
import { DeletesupportComponent } from './routes/assessments/support-requests-tab/delete-support/delete-support.component';
import { PrintReportFundingComponent } from './routes/report-2500-8/funding/print-report-funding/print-report-funding.component';
import { FundingPrintComponent } from './routes/report-2500-8/funding/funding-print/funding-print.component';
import { LinkClickDirective } from './directive/link-click.directive';
import { SendBackReportComponent } from './components/send-back-report/send-back-report.component';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    DashboardComponent,
    NavComponent,
    BannerComponent,
    FireTrackerComponent,
    NewFireComponent,
    EditFireComponent,
    MergeSnackbarComponent,
    MergeFiresComponent,
    ConfirmationToastComponent,
    SuccessToastComponent,
    ErrorToastComponent,
    DataManipulationComponent,
    RosterComponent,
    UserRosterComponent,
    PhonePipe,
    SkillsPipe,
    ForestFilterPipe,
    LineBreakPipe,
    BoolPipe,
    StatusBadgePipe,
    ReportStatusBadgePipe,
    AuthorizationStatusBadgePipe,
    NAFilterPipe,
    NAUnfilterPipe,
    ComplexityModalComponent,
    ProgressSpinnerComponent,
    UserComponent,
    ViewContactComponent,
    ViewAvailabilityComponent,
    ViewSkillsComponent,
    ViewAssignmentsComponent,
    EditContactComponent,
    EditAvailabilityComponent,
    EditSkillsComponent,
    NewUserComponent,
    DeleteUserComponent,
    LoginRedirectComponent,
    DeleteUserSnackbarComponent,
    LoginComponent,
    FocusableDirective,
    RemoveUserComponent,
    HeaderMenuComponent,
    EditSelfComponent,
    EditAssessmentComponent,
    AssessmentsComponent,
    ReportListComponent,
    RequestSupportComponent,
    FireOverviewComponent,
    ReportsTabComponent,
    ViewFireComponent,
    ViewTeamNeedsComponent,
    ViewReportsComponent,
    ViewForestContactsComponent,
    ViewPositionsComponent,
    SupportRequestsTabComponent,
    BaerAssessmentsTabComponent,
    TeamsTabComponent,
    DemobilizeComponent,
    AddTeamMemberComponent,
    RemoveMemberComponent,
    Report25008Component,
    ReportTypeComponent,
    ReportTypeModalComponent,
    FullPhasedModalComponent,
    BurnedAreaComponent,
    WatershedConditionComponent,
    EmergencyStabilizationComponent,
    AnalysisSummaryComponent,
    ImageUploadComponent,
    RecommendedTreatmentComponent,
    BytePipe,
    RecommendedTreatmentComponent,
    TeamSupportingComponent,
    NewCommentComponent,
    DeleteCommentComponent,
    SelectLineOfficerComponent,
    SubmissionComponent,
    LocationFilterComponent,
    ViewReport25008Component,
    ViewReportTypeComponent,
    ViewBurnedAreaComponent,
    ViewWatershedConditionComponent,
    ViewAnalysisSummaryComponent,
    ViewRecommendedTreatmentComponent,
    ViewEmergencyStabilizationComponent,
    ViewTeamSupportingComponent,
    ViewSelectLineOfficerComponent,
    ViewImageComponent,
    ViewFullSizeImageModalComponent,
    DeleteTreatmentComponent,
    EmptyCheckPipe,
    SignReportComponent,
    ViewLOSignaturesComponent,
    RegionalFundingComponent,
    WOFundingComponent,
    EditFundsComponent,
    SelectRegionalOfficerComponent,
    ViewROSignaturesComponent,
    AuthorizationMethodPipe,
    ReportManipulationComponent,
    SelectWOOfficerComponent,
    ViewWOSignaturesComponent,
    AssessmentCostComponent,
    ViewAssessmentCostComponent,
    PrintReport25008Component,
    PrintReportFundingComponent,
    FundingPrintComponent,
    MyDashboardComponent,
    TeamAssignmentsComponent,
    LineAssignmentsComponent,
    ViewRegionalOfficerComponent,
    QuickConfirmSnackbarComponent,
    QuickConfirmSnackbarComponentWO,
    ViewWOOfficerComponent,
    CoorAssignmentsComponent,
    DeleteFireComponent,
    SupportsComponent,
    CurrentStatusComponent,
    StatusRollupComponent,
    StatusSummaryComponent,
    RemovePositionsComponent,
    TreatmentChartComponent,
    AuthStatusComponent,
    AuthReportListComponent,
    BarcRequestComponent,
    AuthFundingComponent,
    FundingTabComponent,
    SupportComponent,
    DeletesupportComponent,
    AnnualCompareComponent,
    AccomplishmentsComponent,
    ReportAccomplishmentComponent,
    AccomplishmentDetailsComponent,
    AccomplishmentTreatmentsComponent,
    AccomplishmentAdditionalComponent,
    AccomplishmentSubmissionComponent,
    ViewReportAccomplishmentComponent,
    ViewAccomplishmentDetailsComponent,
    ViewAccomplishmentTreatmentsComponent,
    ViewAccomplishmentAdditionalComponent,
    PrintReportAccomplishmentComponent,
    SafeIdPipe,
    EditCertificationsComponent,
    ReportsComponent,
    ReportsColumnsComponent,
    AuthorizedActivitiesComponent,
    ExportAuthActivitiesComponent,
    ReportsFiltersComponent,
    SendBackReportComponent,
  ],
  bootstrap: [AppComponent],
  imports: [NgxEditorModule,
    SortablejsDirective,
    // NgxChartsModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FontAwesomeModule,
    FormsModule,
    ReactiveFormsModule,
    MatTableModule,
    MatChip,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelectModule,
    MatTooltipModule,
    MatDialogModule,
    MatSortModule,
    MatButton,
    MatIconButton,
    MatDatepickerModule,
    MatNativeDateModule,
    MatGridListModule,
    MatSnackBarLabel,
    MatSnackBarActions,
    MatSnackBarAction,
    MatCardModule,
    MatIconModule,
    MatExpansionModule,
    MatTabsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatRadioModule,
    MatMenuModule,
    NgxMaskDirective,
    LinkClickDirective,
    MatGridList,
    MatGridTile,
    TextFieldModule,
    MatSidenavModule,
    MatListModule,
    BarHorizontalComponent,
    BarHorizontalStackedComponent
  ],
  providers: [
    provideAnimationsAsync(),
    provideGlobalSortableOptions({ animation: 200 }),
    ConfirmationToastComponent,
    provideNgxMask(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    provideClientHydration(),
    ForestFilterPipe,
    {provide: DateAdapter, useClass: CustomDateAdapter},
    {provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: {timezone: '+0000'}},
    provideHttpClient(withInterceptorsFromDi()),

    NAFilterPipe,
    NAUnfilterPipe,
    SkillsPipe,
  ]
})
export class AppModule {
}
